import * as React from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Typography,
	Box,
	Chip,
	TablePagination,
	TextField,
	MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { currencyFormatter } from "utilities/currencyFormatter";
import { useSelector } from "react-redux";
import { useRentalPayments } from "services/hooks/rentals";
import DummyTable from "components/DummyViews/DummyTable";
import moment from "moment";
 
const StyledTableCell = styled(TableCell)(({ theme }) => ({
	"&.MuiTableCell-head": {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.common.white,
		fontWeight: "bold",
	},
	"&.MuiTableCell-body": {
		fontSize: 14,
	},
}));
 
const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(odd)": {
		backgroundColor: theme.palette.action.hover,
	},
	"&:hover": {
		backgroundColor: theme.palette.action.selected,
	},
	"&:last-child td, &:last-child th": {
		border: 0,
	},
}));
 
const getPaymentStatusChip = (status) => {
	return (
		<Chip
			label={status ? "Paid" : "Pending"}
			color={status ? "success" : "warning"}
			size="small"
		/>
	);
};
 
const getPaymentMethodLabel = (method) => {
	// Payment method may be an object with name property
	if (typeof method === "object" && method !== null) {
		return method.name || "Unknown";
	}
	return "Unknown";
};
 
const months = [
	{ month: "January", value: "01" },
	{ month: "February", value: "02" },
	{ month: "March", value: "03" },
	{ month: "April", value: "04" },
	{ month: "May", value: "05" },
	{ month: "June", value: "06" },
	{ month: "July", value: "07" },
	{ month: "August", value: "08" },
	{ month: "September", value: "09" },
	{ month: "October", value: "10" },
	{ month: "November", value: "11" },
	{ month: "December", value: "12" },
];
 
export default function RentalPayments({ rentalId }) {
	const years = useSelector((state) => state.years);
	const currentMonth = moment().format("MMMM");
	const currentYear = moment().format("YYYY");
	
	// State for filters - default to current month and year
	const [selectedMonth, setSelectedMonth] = React.useState(currentMonth);
	const [selectedYear, setSelectedYear] = React.useState(currentYear);
	
	// Fetch payments for the specific rental_id (no year filter on API call, we'll filter client-side)
	const {
		rentalPayments: allPayments,
		isLoadingRentalPayments,
		errorRentalPayments,
	} = useRentalPayments(rentalId, selectedYear === "All" ? null : selectedYear);
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(10);
 
	// Filter payments based on selected month and year, then sort by date_paid (most recent first)
	const filteredPayments = React.useMemo(() => {
		if (!allPayments || allPayments.length === 0) return [];
		
		const filtered = allPayments.filter((payment) => {
			const datePaid = payment.date_paid || payment.date_paid_display;
			if (!datePaid) return false;
			
			const paymentDate = moment(datePaid);
			const paymentMonth = paymentDate.format("MMMM");
			const paymentYear = paymentDate.format("YYYY");
			
			// If "All" is selected for month or year, don't filter by that field
			const monthMatch = selectedMonth === "All" || paymentMonth === selectedMonth;
			const yearMatch = selectedYear === "All" || paymentYear === selectedYear;
			
			return monthMatch && yearMatch;
		});
		
		// Sort by date_paid in descending order (most recent first)
		return filtered.sort((a, b) => {
			const dateA = moment(a.date_paid || a.date_paid_display);
			const dateB = moment(b.date_paid || b.date_paid_display);
			
			// If dates are invalid, put them at the end
			if (!dateA.isValid() && !dateB.isValid()) return 0;
			if (!dateA.isValid()) return 1;
			if (!dateB.isValid()) return -1;
			
			// Sort in descending order (most recent first)
			return dateB.valueOf() - dateA.valueOf();
		});
	}, [allPayments, selectedMonth, selectedYear]);
 
	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};
 
	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};
 
	const handleMonthChange = (event) => {
		const newMonth = event.target.value;
		setSelectedMonth(newMonth);
		setPage(0); // Reset to first page when filter changes
		
		// If both filters are set to "All", revert to current month/year
		if (newMonth === "All" && selectedYear === "All") {
			setSelectedMonth(currentMonth);
			setSelectedYear(currentYear);
		}
	};
 
	const handleYearChange = (event) => {
		const newYear = event.target.value;
		setSelectedYear(newYear);
		setPage(0); // Reset to first page when filter changes
		
		// If both filters are set to "All", revert to current month/year
		if (selectedMonth === "All" && newYear === "All") {
			setSelectedMonth(currentMonth);
			setSelectedYear(currentYear);
		}
	};
 
	// Calculate summary statistics based on filtered payments
	const totalAmount = filteredPayments.reduce(
		(sum, payment) => sum + parseFloat(payment.amount || 0),
		0
	);
	const totalPayments = filteredPayments.length;
	const paidPayments = filteredPayments.filter((p) => p.status).length;
	const pendingPayments = totalPayments - paidPayments;
 
	if (isLoadingRentalPayments) {
		return <DummyTable />;
	}
 
	if (errorRentalPayments) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography variant="h6" color="error">
					Error loading rental payments. Please try again later.
				</Typography>
			</Box>
		);
	}
 
	return (
		<Box sx={{ p: 2 }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
					flexWrap: "wrap",
					gap: 2,
				}}
			>
				<Typography variant="h4" sx={{ fontWeight: "bold" }}>
					Rental Payments
				</Typography>
				
				{/* Filter Controls */}
				<Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
					<Typography variant="body1" sx={{ fontWeight: 500 }}>
						Filter:
					</Typography>
					<TextField
						label="Month"
						size="small"
						select
						value={selectedMonth}
						onChange={handleMonthChange}
						sx={{ minWidth: 150 }}
					>
						<MenuItem value="All">All Months</MenuItem>
						{months.map((month, index) => (
							<MenuItem key={index} value={month.month}>
								{month.month}
							</MenuItem>
						))}
					</TextField>
					<TextField
						label="Year"
						size="small"
						select
						value={selectedYear}
						onChange={handleYearChange}
						sx={{ minWidth: 150 }}
					>
						<MenuItem value="All">All Years</MenuItem>
						{(years || []).map((year, index) => (
							<MenuItem key={index} value={year.toString()}>
								{year}
							</MenuItem>
						))}
					</TextField>
				</Box>
			</Box>
 
			{/* Summary Cards */}
			<Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
				<Paper sx={{ p: 2, minWidth: 200, textAlign: "center" }}>
					<Typography variant="h6" color="primary" fontWeight="bold">
						{currencyFormatter(totalAmount)}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Total Amount Collected
					</Typography>
				</Paper>
				<Paper sx={{ p: 2, minWidth: 200, textAlign: "center" }}>
					<Typography variant="h6" color="success.main" fontWeight="bold">
						{paidPayments}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Paid Payments
					</Typography>
				</Paper>
				<Paper sx={{ p: 2, minWidth: 200, textAlign: "center" }}>
					<Typography variant="h6" color="warning.main" fontWeight="bold">
						{pendingPayments}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Pending Payments
					</Typography>
				</Paper>
				<Paper sx={{ p: 2, minWidth: 200, textAlign: "center" }}>
					<Typography variant="h6" color="secondary" fontWeight="bold">
						{totalPayments}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Total Payments
					</Typography>
				</Paper>
			</Box>
 
			{/* Table */}
			<TableContainer component={Paper}>
				<Table sx={{ minWidth: 650 }} aria-label="rental payments table">
					<TableHead>
						<TableRow>
							<StyledTableCell>Payment ID</StyledTableCell>
							<StyledTableCell>Tenant Name</StyledTableCell>
							<StyledTableCell>Unit Name</StyledTableCell>
							<StyledTableCell>Property Name</StyledTableCell>
							<StyledTableCell>Amount</StyledTableCell>
							<StyledTableCell>Unit Rent</StyledTableCell>
							<StyledTableCell>Period</StyledTableCell>
							<StyledTableCell>Date Paid</StyledTableCell>
							<StyledTableCell>Payment Method</StyledTableCell>
							<StyledTableCell>Status</StyledTableCell>
							<StyledTableCell>Reference</StyledTableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredPayments.length > 0 ? (
							filteredPayments
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map((payment) => (
									<StyledTableRow key={payment.id}>
										<TableCell>{payment.id}</TableCell>
										<TableCell>
											{payment.related_tenant?.first_name} {payment.related_tenant?.last_name}
										</TableCell>
										<TableCell>{payment.related_rental_unit?.unit_name}</TableCell>
										<TableCell>{payment.related_rental_unit?.related_rental?.rental_name}</TableCell>
										<TableCell>
											<Typography fontWeight="bold">
												{currencyFormatter(parseFloat(payment.amount || 0))}
											</Typography>
										</TableCell>
										<TableCell>
											{currencyFormatter(parseFloat(payment.unit_rent || 0))}
										</TableCell>
										<TableCell>
											{payment.related_month?.month} {payment.related_month?.year}
										</TableCell>
										<TableCell>
											{payment.date_paid_display || payment.date_paid || "N/A"}
										</TableCell>
										<TableCell>{getPaymentMethodLabel(payment.payment_method)}</TableCell>
										<TableCell>{getPaymentStatusChip(payment.status)}</TableCell>
										<TableCell>
											<Typography variant="caption" sx={{ fontFamily: "monospace" }}>
												{payment.external_reference || "N/A"}
											</Typography>
										</TableCell>
									</StyledTableRow>
								))
						) : (
							<TableRow>
								<TableCell colSpan={11} align="center">
									<Typography variant="body1" color="text.secondary">
										No rental payments found
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
				<TablePagination
					rowsPerPageOptions={[10, 25, 50, 100]}
					component="div"
					count={filteredPayments.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</TableContainer>
		</Box>
	);
}
